'use client';

import { useEffect, useRef } from 'react';
import styles from './PerfectMixIntroSection.module.scss';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export interface PerfectMixIntroSectionProps {
  title?: string;
  titleSide?: string;
  highlightedTitle?: string;
  description?: string;
}

const renderMarked = (text: string, highlightDataAttr?: string) => {
  const parts = text.split('*');
  let emIndex = 0;
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      emIndex += 1;
      return (
        <em key={i} {...(highlightDataAttr && emIndex === 1 ? { [highlightDataAttr]: '' } : {})}>
          {part}
        </em>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

export const PerfectMixIntroSection = ({
  title = 'Ressources *sur-mesure*',
  titleSide = '— Pas au forfait —',
  highlightedTitle = 'Au *résultat*.',
  description,
}: PerfectMixIntroSectionProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !rootRef.current) {
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const root = rootRef.current;
    const ctx = gsap.context(() => {
      /* ── "sur-mesure" highlight reveal ── */
      gsap.set('[data-highlight-reveal]', { opacity: 0, y: 20, scale: 0.95 });
      gsap.to('[data-highlight-reveal]', {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.3,
        ease: 'back.out(1.4)',
        scrollTrigger: {
          trigger: root,
          start: 'top 70%',
          end: '+=35%',
          scrub: 0.5,
        },
      });

      /* ── "Au résultat" dramatic reveal ── */
      gsap.set('[data-result-reveal]', { opacity: 0, y: 30, scale: 0.92 });
      gsap.to('[data-result-reveal]', {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.35,
        ease: 'back.out(1.6)',
        scrollTrigger: {
          trigger: root,
          start: 'top 55%',
          end: '+=35%',
          scrub: 0.5,
        },
      });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={rootRef} className={styles.root} data-mix-intro>
      <h2 className={styles.sectionTitle}>
        <span className={styles.titleLine1}>{renderMarked(title, 'data-highlight-reveal')}</span>
        <span className={styles.titleSide}>{titleSide}</span>
        <span className={styles.titleLine3} data-result-reveal>
          {renderMarked(highlightedTitle)}
        </span>
      </h2>
      <div className={styles.description}>
        {description ? (
          description.split('\n\n').map((paragraph, i) => <p key={i}>{paragraph}</p>)
        ) : (
          <>
            <p>
              Nous ne plaçons pas des profils — nous prenons le commandement de votre projet et le
              livrons dans les délais.
            </p>
            <p>
              Le staffing et l&apos;orchestration sont de notre côté. Vous avez un seul
              interlocuteur responsable, et l&apos;avancée de chaque tâche, ticket et livrable reste
              transparente en permanence.
            </p>
          </>
        )}
      </div>
    </div>
  );
};
